import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, X, Layers, Globe, Hash, Info, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { customerTranslationApi } from '../api/api';

// Section configuration with descriptions for clarity
const SECTION_CONFIG = {
    loginModal: { label: 'Login Modal', icon: '🔐', description: 'Text shown in the login popup' },
    registerModal: { label: 'Register Modal', icon: '📝', description: 'Text for user registration' },
    forgotPassword: { label: 'Forgot Password', icon: '🔑', description: 'Password reset flow text' },
    navbar: { label: 'Navigation Bar', icon: '🧭', description: 'Top menu and links' },
    footer: { label: 'Footer', icon: '📋', description: 'Bottom page content' },
    sidebar: { label: 'Sidebar', icon: '📱', description: 'User account sidebar navigation' },
    homePage: { label: 'Home Page', icon: '🏠', description: 'Landing page content' },
    restaurantList: { label: 'Restaurant List', icon: '🔍', description: 'Restaurant search and browse page' },
    restaurantDetail: { label: 'Restaurant Detail', icon: '🍽️', description: 'Restaurant info pages' },
    reservationFlow: { label: 'Reservation Flow', icon: '📅', description: 'Booking process text' },
    profilePage: { label: 'Profile Page', icon: '👤', description: 'User profile settings' },
    reservationsPage: { label: 'My Reservations', icon: '📖', description: 'User reservations list' },
    reviewsPage: { label: 'Reviews Page', icon: '⭐', description: 'Reviews and ratings' },
    favoritesPage: { label: 'Favorites Page', icon: '❤️', description: 'Saved restaurants' },
    aboutPage: { label: 'About Page', icon: 'ℹ️', description: 'About Avopla content' },
    contactPage: { label: 'Contact Page', icon: '📧', description: 'Contact information' },
    faqPage: { label: 'FAQ Page', icon: '❓', description: 'Frequently asked questions' },
    notFound: { label: '404 Page', icon: '🔍', description: 'Page not found error' },
    verifyEmail: { label: 'Verify Email', icon: '✉️', description: 'Email verification page' },
    terms: { label: 'Terms & Conditions', icon: '📜', description: 'Terms and conditions page' },
    privacy: { label: 'Privacy Policy', icon: '🔒', description: 'Privacy policy page' },
    common: { label: 'Common/Shared', icon: '🔧', description: 'Reusable text across pages' }
};

const SAMPLE_TRANSLATIONS = [
    { id: '1', section: 'loginModal', key: 'signInTitle', en: 'SIGN-IN', fr: 'CONNEXION', description: 'Login modal title', updatedAt: new Date() },
    { id: '2', section: 'loginModal', key: 'welcomeToAvopla', en: 'Welcome to Avopla', fr: 'Bienvenue chez Avopla', description: 'Welcome message', updatedAt: new Date() },
];

const CustomerTranslations = () => {
    const [translations, setTranslations] = useState([]);
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSection, setActiveSection] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [editingTranslation, setEditingTranslation] = useState(null);
    const [formData, setFormData] = useState({
        section: 'loginModal',
        key: '',
        en: '',
        fr: '',
        description: ''
    });

    useEffect(() => {
        fetchTranslations();
        fetchSections();
    }, []);

    const fetchSections = async () => {
        try {
            const response = await customerTranslationApi.getSections();
            setSections(response.data);
        } catch (error) {
            setSections(Object.keys(SECTION_CONFIG));
        }
    };

    const fetchTranslations = async () => {
        try {
            setLoading(true);
            const response = await customerTranslationApi.getAll();
            setTranslations(response.data);
        } catch (error) {
            setTranslations(SAMPLE_TRANSLATIONS);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingTranslation) {
                await customerTranslationApi.update(editingTranslation.id, formData);
                toast.success('Translation updated');
            } else {
                await customerTranslationApi.create(formData);
                toast.success('Translation created');
            }
            setShowModal(false);
            setEditingTranslation(null);
            resetForm();
            fetchTranslations();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save');
        }
    };

    const handleEdit = (translation) => {
        setEditingTranslation(translation);
        setFormData({
            section: translation.section,
            key: translation.key,
            en: translation.en,
            fr: translation.fr,
            description: translation.description || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this translation?')) return;
        try {
            await customerTranslationApi.delete(id);
            toast.success('Translation deleted');
            fetchTranslations();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const resetForm = () => {
        setFormData({ section: 'loginModal', key: '', en: '', fr: '', description: '' });
    };

    const openNewModal = () => {
        setEditingTranslation(null);
        resetForm();
        setShowModal(true);
    };

    const filteredTranslations = translations.filter(t => {
        const matchesSection = activeSection === 'all' || t.section === activeSection;
        const matchesSearch = searchTerm === '' ||
            t.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
            t.en.toLowerCase().includes(searchTerm.toLowerCase()) ||
            t.fr.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSection && matchesSearch;
    });

    const groupedTranslations = filteredTranslations.reduce((acc, t) => {
        if (!acc[t.section]) acc[t.section] = [];
        acc[t.section].push(t);
        return acc;
    }, {});

    const sectionCounts = translations.reduce((acc, t) => {
        acc[t.section] = (acc[t.section] || 0) + 1;
        return acc;
    }, {});

    const getSectionConfig = (section) => SECTION_CONFIG[section] || { label: section, icon: '📄' };

    if (loading) {
        return (
            <div className="loading-overlay">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', gap: '24px', minHeight: 'calc(100vh - 120px)' }}>
            {/* Section Navigation */}
            <div className="section-nav" style={{ width: '260px', flexShrink: 0, height: 'fit-content', position: 'sticky', top: '20px' }}>
                <div className="section-nav-header">
                    <div className="section-nav-icon">
                        <Layers size={16} />
                    </div>
                    <div>
                        <div className="section-nav-title">Sections</div>
                        <div className="section-nav-subtitle">{sections.length} categories</div>
                    </div>
                </div>

                <button
                    onClick={() => setActiveSection('all')}
                    className={`section-btn ${activeSection === 'all' ? 'active' : ''}`}
                >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Globe size={14} />
                        <span className="section-btn-label">All Translations</span>
                    </span>
                    <span className="section-btn-count">{translations.length}</span>
                </button>

                <div style={{ marginTop: '8px' }}>
                    {sections.map(section => {
                        const config = getSectionConfig(section);
                        const count = sectionCounts[section] || 0;
                        return (
                            <button
                                key={section}
                                onClick={() => setActiveSection(section)}
                                className={`section-btn ${activeSection === section ? 'active' : ''}`}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span className="section-btn-icon">{config.icon}</span>
                                    <span className="section-btn-label">{config.label}</span>
                                </span>
                                {count > 0 && <span className="section-btn-count">{count}</span>}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Main Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
                {/* Header with Clear Description */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px' }}>
                    <div>
                        <h1 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '6px' }}>
                            Customer Website
                        </h1>
                        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '0' }}>
                            Manage translations for customer-facing pages
                        </p>
                    </div>
                    <button
                        className="btn btn-primary"
                        onClick={openNewModal}
                        title="Create a new translation entry"
                    >
                        <Plus size={16} />
                        Add Translation
                    </button>
                </div>

                {/* Search */}
                <div className="search-bar" style={{ marginBottom: '24px' }}>
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search by key, English or French text..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setSearchTerm('')}
                            style={{ marginLeft: 'auto' }}
                        >
                            Clear
                        </button>
                    )}
                </div>

                {/* Translations */}
                {filteredTranslations.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">
                            <Globe size={28} />
                        </div>
                        <h3>No translations found</h3>
                        <p>{searchTerm ? 'Try a different search' : 'Add your first translation'}</p>
                        <button className="btn btn-primary" onClick={openNewModal}>
                            <Plus size={16} />
                            Add Translation
                        </button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {Object.entries(groupedTranslations).map(([section, items]) => {
                            const config = getSectionConfig(section);
                            return (
                                <div key={section} className="translation-group">
                                    {/* Section Header with Description */}
                                    <div className="translation-group-header">
                                        <div className="translation-group-title">
                                            <span className="translation-group-icon">{config.icon}</span>
                                            <div>
                                                <span className="translation-group-name">{config.label}</span>
                                                {config.description && (
                                                    <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'block', marginTop: '2px' }}>
                                                        {config.description}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <span className="translation-group-count">{items.length} items</span>
                                    </div>

                                    {items.map(t => (
                                        <div key={t.id} className="translation-item">
                                            <div className="translation-item-header">
                                                <div>
                                                    <span className="translation-key">
                                                        <Hash size={12} />
                                                        {t.key}
                                                    </span>
                                                    {t.description && (
                                                        <p className="translation-description">{t.description}</p>
                                                    )}
                                                </div>
                                                {/* Action Buttons with Clear Labels */}
                                                <div className="translation-actions">
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        onClick={() => handleEdit(t)}
                                                        title="Edit this translation"
                                                    >
                                                        <Edit2 size={14} />
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="btn btn-danger btn-icon btn-sm"
                                                        onClick={() => handleDelete(t.id)}
                                                        title="Delete this translation permanently"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Language Cards */}
                                            <div className="translation-languages">
                                                <div className="translation-lang">
                                                    <div className="translation-lang-header">
                                                        <span className="translation-lang-flag">🇬🇧</span>
                                                        ENGLISH
                                                    </div>
                                                    <div className={`translation-lang-text ${!t.en ? 'translation-lang-empty' : ''}`}>
                                                        {t.en || 'Not set'}
                                                    </div>
                                                </div>
                                                <div className="translation-lang">
                                                    <div className="translation-lang-header">
                                                        <span className="translation-lang-flag">🇫🇷</span>
                                                        FRENCH
                                                    </div>
                                                    <div className={`translation-lang-text ${!t.fr ? 'translation-lang-empty' : ''}`}>
                                                        {t.fr || 'Not set'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingTranslation ? 'Edit Translation' : 'Add Translation'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">Section</label>
                                    <select
                                        className="form-select"
                                        value={formData.section}
                                        onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                                        required
                                    >
                                        {sections.map(s => (
                                            <option key={s} value={s}>
                                                {getSectionConfig(s).icon} {getSectionConfig(s).label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Translation Key</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={formData.key}
                                        onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                                        placeholder="e.g., signInTitle"
                                        required
                                        style={{ fontFamily: 'monospace' }}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">🇬🇧 English Text</label>
                                    <textarea
                                        className="form-textarea"
                                        value={formData.en}
                                        onChange={(e) => setFormData({ ...formData, en: e.target.value })}
                                        placeholder="Enter English translation..."
                                        required
                                        rows={3}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">🇫🇷 French Text</label>
                                    <textarea
                                        className="form-textarea"
                                        value={formData.fr}
                                        onChange={(e) => setFormData({ ...formData, fr: e.target.value })}
                                        placeholder="Enter French translation..."
                                        required
                                        rows={3}
                                    />
                                </div>

                                <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label className="form-label">
                                        Description <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Brief note about where this text appears"
                                    />
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingTranslation ? 'Save Changes' : 'Add Translation'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerTranslations;
